import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { closeModal } from "$stores/modal";
import { initiativesApi } from "../api";
import SelectTargetStep from "./steps/SelectTargetStep";
import DateInput from "$components/form-inputs/date-picker";
import { getTerm } from "$stores/dictionary";

import "./initiative-wizard.scss";

const InitiativeWizard = ({ isOpen }) => {
    const [step, setStep] = useState(0);
    const [form, setForm] = useState({ name: "", summaryText: "", targetDate: null, focusType: "", focusTarget: { items: [] } });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const navigate = useNavigate();
    const nameInputRef = useRef(null);

    const focusTypes = [
        { value: "PartnerClients", label: `${getTerm("parentCrm")}'s ${getTerm("parentClients")}`,     description: `${getTerm("potentialProjects")} across a specific ${getTerm("parentCrm").toLowerCase()}'s ${getTerm("parentClient").toLowerCase()} portfolio` },
        { value: "PracticeArea",   label: getTerm("serviceLine"),                                       description: `${getTerm("potentialProjects")} within a specific ${getTerm("serviceLine").toLowerCase()}` },
        { value: "Industry",       label: getTerm("industry"),                                          description: `${getTerm("potentialProjects")} across a specific ${getTerm("industry").toLowerCase()}` },
        { value: "SelectClients",  label: `Select ${getTerm("parentClients")}`,                         description: `Hand-pick a list of specific ${getTerm("parentClients").toLowerCase()} for this initiative` },
        { value: "MatterType",     label: getTerm("projectType"),                                        description: `${getTerm("potentialProjects")} of a specific ${getTerm("projectType").toLowerCase()}` },
    ];

    // Retained for backward compat display on existing initiatives
    const focusTypeLabels = {
        PartnerClients: `${getTerm("parentCrm")}'s ${getTerm("parentClients")}`,
        PracticeArea:   getTerm("serviceLine"),
        Industry:       getTerm("industry"),
        SelectClients:  `Select ${getTerm("parentClients")}`,
        TopClients:     `Top ${getTerm("parentClients")}`,
        AtRiskClients:  `At-Risk ${getTerm("parentClients")}`,
        MatterType:     getTerm("projectType"),
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => nameInputRef.current?.focus(), 50);
        } else {
            setStep(0);
            setForm({ name: "", summaryText: "", targetDate: null, focusType: "", focusTarget: { items: [] } });
            setSaving(false);
            setSaveError(null);
        }
    }, [isOpen]);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));
    const next = () => setStep((s) => Math.min(s + 1, 3));
    const back = () => setStep((s) => Math.max(s - 1, 0));

    const create = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const res = await initiativesApi.create({
                name: form.name,
                summaryText: form.summaryText.trim() || null,
                targetDate: form.targetDate ? form.targetDate.toISOString() : null,
                focusType: form.focusType,
                focusTargetJson: JSON.stringify({ ids: form.focusTarget.items.map((i) => i.value), items: form.focusTarget.items }),
            });
            closeModal();
            navigate(`/initiatives/${res.id}`);
        } catch {
            setSaveError("Something went wrong. Please try again.");
            setSaving(false);
        }
    };

    const w = (n) => classNames("wizard-widget", { "-active": step === n, "-previous": step > n });

    const showBack = step > 0;
    const showNext = step === 0 || step === 2;
    const showCreate = step === 3;
    const nextDisabled = (step === 0 && !form.name.trim()) || (step === 2 && form.focusTarget.items.length === 0);

    return (
        <div className="wizard initiative-wizard">
            {/* Persistent orientation header */}
            <div className="initiative-wizard__header">
                <span className="initiative-wizard__title">{getTerm("initiative")} Setup</span>
            </div>

            {/* Step 0: Initiative details */}
            <div className={w(0)}>
                <h3>Name &amp; Details</h3>
                <p>An initiative is a focused campaign to grow revenue across a defined set of {getTerm("parentClients").toLowerCase()} or {getTerm("potentialProjects").toLowerCase()}. Give it a name and an optional goal and deadline.</p>
                <div className="form-field -full">
                    <label>Name</label>
                    <input
                        ref={nameInputRef}
                        type="text"
                        value={form.name}
                        onChange={(e) => update({ name: e.target.value })}
                        onKeyDown={(e) => e.key === "Enter" && form.name.trim() && next()}
                        placeholder="e.g., Partner Smith Q2 Review"
                    />
                </div>
                <div className="form-field -full">
                    <label>Summary <span className="txt -small -gray">(optional)</span></label>
                    <textarea
                        value={form.summaryText}
                        onChange={(e) => update({ summaryText: e.target.value })}
                        placeholder="Briefly describe the goal of this initiative"
                    />
                </div>
                <div className="date-field-wrapper">
                    <DateInput
                        id="targetDate"
                        label="Target date"
                        defaultValue={form.targetDate}
                        updateOnChange={(id, date) => update({ targetDate: date })}
                    />
                    <FontAwesomeIcon icon={faCalendarAlt} className="date-calendar-icon" aria-hidden="true" />
                </div>
            </div>

            {/* Step 1: Focus Type */}
            <div className={w(1)}>
                <h3>Choose a Focus Type</h3>
                <p>Select how you want to define the scope of this initiative.</p>
                <div className="initiative-focus-options">
                    {focusTypes.map((ft) => (
                        <div
                            key={ft.value}
                            className={classNames("initiative-focus-option", { "-selected": form.focusType === ft.value })}
                            onClick={() => {
                                update({ focusType: ft.value, focusTarget: { items: [] } });
                                next();
                            }}
                        >
                            <strong>{ft.label}</strong>
                            <span>{ft.description}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Step 2: Select Target */}
            <div className={w(2)}>
                <SelectTargetStep
                    focusType={form.focusType}
                    value={form.focusTarget}
                    onChange={(v) => update({ focusTarget: v })}
                />
            </div>

            {/* Step 3: Review & Create */}
            <div className={w(3)}>
                <h3>Review &amp; Create</h3>
                <dl className="review-summary">
                    <dt>Name</dt>
                    <dd>{form.name}</dd>
                    {form.summaryText.trim() && (
                        <>
                            <dt>Summary</dt>
                            <dd>{form.summaryText.trim()}</dd>
                        </>
                    )}
                    {form.targetDate && (
                        <>
                            <dt>Target Date</dt>
                            <dd>{form.targetDate.toLocaleDateString()}</dd>
                        </>
                    )}
                    <dt>Focus Type</dt>
                    <dd>{focusTypeLabels[form.focusType] || form.focusType}</dd>
                    <dt>Selected</dt>
                    <dd>
                        <span className="review-selected-count">{form.focusTarget?.items?.length ?? 0} selected</span>
                        {form.focusTarget?.items?.length > 0 && (
                            <ul className="review-selected-list">
                                {form.focusTarget.items.map((item) => (
                                    <li key={item.value}>{item.text}</li>
                                ))}
                            </ul>
                        )}
                    </dd>
                </dl>
            </div>

            {/* Pinned footer — direct child of .wizard so global position:absolute;bottom:0 applies */}
            <div className="finalize">
                {saveError && <p className="txt -error">{saveError}</p>}
                <div className="button-group -right">
                    {showBack && (
                        <button className="button -link" onClick={back} disabled={saving}>Back</button>
                    )}
                    {showNext && (
                        <button className="button -primary" disabled={nextDisabled || saving} onClick={next}>Next</button>
                    )}
                    {showCreate && (
                        <button className="button -primary" onClick={create} disabled={saving}>
                            {saving ? "Creating..." : `Create ${getTerm("initiative")}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InitiativeWizard;
